import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Radio, Checkbox, Button, Space, Tag, Switch, DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  isMemberOnlineNow,
  wasMemberOnlineToday,
  getMemberSourceLabel
} from '../utils/dashboard/memberMarkers';

function memberKey(member) {
  return String(member?.device_unique_id || member?.name || '');
}

/**
 * 会标 / 轨迹发送位置：携带消息 + 所有人 / 自定义队员
 */
export default function DashboardSendLocationComposeModal({
  open,
  planted,
  presetMember,
  members = [],
  onlineTimeoutMs,
  sending = false,
  onCancel,
  onSubmit
}) {
  const [note, setNote] = useState('请前往此位置');
  const [important, setImportant] = useState(true);
  const [scope, setScope] = useState('all');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [expiresAt, setExpiresAt] = useState(() => dayjs().add(24, 'hour'));

  useEffect(() => {
    if (!open) return;
    setNote('请前往此位置');
    setImportant(true);
    setPickerOpen(false);
    setOnlineOnly(false);
    setKeyword('');
    setExpiresAt(dayjs().add(24, 'hour'));
    const named = presetMember?.name && !presetMember.rally;
    if (named) {
      setScope('custom');
      setSelectedKeys([memberKey(presetMember)]);
    } else {
      setScope('all');
      setSelectedKeys([]);
    }
  }, [open, presetMember?.name, presetMember?.rally]);

  const todayMembers = useMemo(
    () => (members || []).filter((m) => m?.name && wasMemberOnlineToday(m)),
    [members]
  );

  const selectedMembers = useMemo(() => {
    const set = new Set(selectedKeys);
    return (members || []).filter((m) => set.has(memberKey(m)));
  }, [members, selectedKeys]);

  const pickerList = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return todayMembers
      .filter((m) => {
        if (onlineOnly && !isMemberOnlineNow(m, Date.now(), onlineTimeoutMs)) return false;
        if (!q) return true;
        const hay = `${m.name || ''} ${m.group_name || ''} ${m.org_name || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ao = isMemberOnlineNow(a, Date.now(), onlineTimeoutMs) ? 0 : 1;
        const bo = isMemberOnlineNow(b, Date.now(), onlineTimeoutMs) ? 0 : 1;
        if (ao !== bo) return ao - bo;
        return String(a.name || '').localeCompare(String(b.name || ''), 'zh');
      });
  }, [todayMembers, onlineOnly, keyword, onlineTimeoutMs]);

  const coordText = planted
    ? `${Number(planted.lat).toFixed(6)}, ${Number(planted.lng).toFixed(6)}`
    : '尚未放置会标';

  const handleOk = () => {
    onSubmit?.({
      note,
      important,
      toAll: scope === 'all',
      selectedMembers,
      expires_at: expiresAt && expiresAt.isValid() ? expiresAt.toISOString() : dayjs().add(24, 'hour').toISOString()
    });
  };

  const toggleKey = (key, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return [...next];
    });
  };

  const selectVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      pickerList.forEach((m) => next.add(memberKey(m)));
      return [...next];
    });
  };

  return (
    <>
      <Modal
        title="发送位置"
        open={open}
        onCancel={onCancel}
        destroyOnClose
        centered
        width={440}
        footer={(
          <Space>
            <Button onClick={onCancel}>取消</Button>
            <Button type="primary" loading={sending} disabled={!planted} onClick={handleOk}>
              发送
            </Button>
          </Space>
        )}
      >
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          坐标（WGS84）：{coordText}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>携带消息</div>
        <Input.TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={200}
          placeholder="发给 Locus 与单兵工具的说明，可留空"
        />
        <div style={{ marginTop: 10 }}>
          <Checkbox checked={important} onChange={(e) => setImportant(e.target.checked)}>
            重要
          </Checkbox>
        </div>
        
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>有效时间</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              value={expiresAt}
              onChange={(v) => setExpiresAt(v)}
              allowClear={false}
              style={{ flex: 1 }}
              placeholder="选择有效截止时间"
            />
            <Button size="small" onClick={() => setExpiresAt(dayjs().add(24, 'hour'))}>
              默认24小时
            </Button>
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
            过期后不再作为活跃任务点展示；默认当前起 24 小时。
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 8px' }}>发送对象</div>
        <Radio.Group
          value={scope}
          onChange={(e) => {
            const next = e.target.value;
            setScope(next);
            setPickerOpen(next === 'custom');
          }}
        >
          <Radio value="all">所有人</Radio>
          <Radio value="custom">自定义</Radio>
        </Radio.Group>
        {scope === 'custom' && (
          <div style={{ marginTop: 10 }}>
            <Button size="small" onClick={() => setPickerOpen(true)}>
              选择队员（当日上线 {todayMembers.length}）
            </Button>
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {selectedMembers.length === 0 ? (
                <span style={{ fontSize: 12, color: '#94a3b8' }}>尚未勾选队员</span>
              ) : selectedMembers.map((m) => (
                <Tag key={memberKey(m)} closable onClose={() => toggleKey(memberKey(m), false)}>
                  {m.name}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="选择队员"
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        destroyOnClose
        centered
        width={420}
        footer={(
          <Space>
            <Button onClick={() => setSelectedKeys([])}>清空</Button>
            <Button onClick={selectVisible}>全选当前列表</Button>
            <Button type="primary" onClick={() => setPickerOpen(false)}>确定</Button>
          </Space>
        )}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>当日上过线的队员</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            在线队员
            <Switch size="small" checked={onlineOnly} onChange={setOnlineOnly} />
          </span>
        </div>
        <Input
          allowClear
          size="small"
          placeholder="搜索姓名 / 小组"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{ marginBottom: 10 }}
        />
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {pickerList.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8', padding: '16px 0' }}>
              {onlineOnly ? '当前没有在线队员' : '今日还没有队员上过线'}
            </div>
          ) : pickerList.map((m) => {
            const key = memberKey(m);
            const online = isMemberOnlineNow(m, Date.now(), onlineTimeoutMs);
            return (
              <label
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 4px',
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer'
                }}
              >
                <Checkbox
                  checked={selectedKeys.includes(key)}
                  onChange={(e) => toggleKey(key, e.target.checked)}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{m.name}</span>
                  <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>
                    {[m.group_name, getMemberSourceLabel(m)].filter(Boolean).join(' · ')}
                    {online ? ' · 在线' : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </Modal>
    </>
  );
}
